import React, { useEffect, useState } from "react";
import { Table, Spinner, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import moment from "moment";
import { API_URL } from "../..";
import { PDFDownloadLink } from "@react-pdf/renderer";
import PayrollPDFCustom from "./PayrollPDFCustom";
import EditAttendanceModal from "./EditAttendanceModal";
import { Icon } from "@iconify/react";

const TimesheetCustomRangeView = ({ startDate, endDate, employee }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editRecord, setEditRecord] = useState(null);

  const today = moment().startOf("day");

  useEffect(() => {
    if (!employee) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_URL}/api/attendance/timesheet-log/custom-range?employeeId=${employee}&startDate=${moment(
            startDate
          ).format("YYYY-MM-DD")}&endDate=${moment(endDate).format("YYYY-MM-DD")}`
        );
        const json = await res.json();
        if (json.success && json.data) {
          setData(json.data);
        } else {
          setData(null);
        }
      } catch (err) {
        console.error("Error loading custom range:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, employee]);

  const handleEditClick = (record) => {
    setEditRecord(record);
  };

  const canEdit = (date) => !moment(date).isAfter(today);

  if (!employee)
    return <div className="alert alert-warning">Please select an employee.</div>;

  if (loading)
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );

  if (!data?.attendance?.length)
    return (
      <div className="alert alert-light">
        No attendance found for this range.
      </div>
    );

  const { user, attendance, summary } = data;

  const formatTime = (t) => (t ? moment(t, "HH:mm:ss").format("hh:mm A") : "--");
  const toNumber = (v) => (isNaN(v) || v === null ? 0 : Number(v));
  const formatMoney = (v) => `£${toNumber(v).toFixed(2)}`;

  // Enhanced attendance with overtime pay display
  const enrichedAttendance = attendance.map((r) => {
    let overtime_pay_display = "--";
    const overtime = r.attendanceOvertimes?.[0];

    if (overtime && overtime.status === "approved") {
      if (overtime.fixed_overtime_amount != null) {
        const amount = parseFloat(overtime.fixed_overtime_amount);
        overtime_pay_display = (
          <span className="text-success fw-bold">
            {formatMoney(amount)} <small className="text-muted">(fixed)</small>
          </span>
        );
      } else if (overtime.overtime_rate_per_hour != null) {
        const rate = parseFloat(overtime.overtime_rate_per_hour);
        const parts = overtime.total_duration.split(":");
        const h = parseInt(parts[0] || 0, 10);
        const m = parseInt(parts[1] || 0, 10);
        const s = parseInt(parts[2] || 0, 10);
        const hours = h + m / 60 + s / 3600;
        const total = rate * hours;

        overtime_pay_display = (
          <span className="text-primary fw-bold">
            {formatMoney(rate)}/hr × {hours.toFixed(2)}h ={" "}
            <span className="text-success">{formatMoney(total)}</span>
          </span>
        );
      }
    }

    return { ...r, overtime_pay_display };
  });

  return (
    <div className="card shadow-sm">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5>
          Custom Range Payroll ({moment(startDate).format("DD MMM")} –{" "}
          {moment(endDate).format("DD MMM YYYY")})
        </h5>

        <PDFDownloadLink
          document={
            <PayrollPDFCustom
              user={user}
              records={enrichedAttendance}
              summary={summary}
              startDate={moment(startDate)}
              endDate={moment(endDate)}
            />
          }
          fileName={`Payroll_${user.name}_${moment(startDate).format("YYYYMMDD")}-${moment(endDate).format("YYYYMMDD")}.pdf`}
        >
          {({ loading }) =>
            loading ? (
              <Button variant="outline-primary" disabled>
                <Spinner animation="border" size="sm" className="me-2" /> Generating...
              </Button>
            ) : (
              <Button variant="outline-primary">
                <i className="bi bi-file-earmark-pdf me-2"></i>Download PDF
              </Button>
            )
          }
        </PDFDownloadLink>
      </div>

      <div className="card-body">
        <div className="text-left mb-4">
          <img src="/assets/images/logo.png" alt="Logo" style={{ height: 60 }} className="mb-3" />
          <h4 className="fw-bold mb-1">{user.name}</h4>
          <p className="mb-1">
            Payroll Type: <strong className="text-capitalize">{user.payroll_type}</strong>
          </p>
          <p className="mb-1">
            Rate: <strong>
              £{user.rate}{" "}
              {user.payroll_type === "hourly" ? "/hr" : user.payroll_type === "daily" ? "/day" : "/month"}
            </strong>
          </p>
          <p className="text-muted small mb-0">
            Period: {moment(startDate).format("DD MMM YYYY")} – {moment(endDate).format("DD MMM YYYY")}
          </p>
        </div>

        {/* Table with Overtime Pay */}
        <Table bordered hover className="text-center align-middle">
          <thead className="table-light">
            <tr>
              <th>Date</th>
              <th>Punch In</th>
              <th>Punch Out</th>
              <th>Work Hours</th>
              <th>Approved Hours</th>
              <th>Overtime Pay</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {enrichedAttendance.map((r) => {
              const isFuture = moment(r.attendance_date || r.date).isAfter(today);

              return (
                <tr key={r.id || r.date}>
                  <td>{moment(r.attendance_date || r.date).format("ddd, DD MMM")}</td>
                  <td>{formatTime(r.punch_in_time)}</td>
                  <td>{formatTime(r.punch_out_time)}</td>
                  <td>{toNumber(r.total_work_hours).toFixed(2)}</td>
                  <td>{toNumber(r.approved_hours).toFixed(2)}</td>
                  <td>{r.overtime_pay_display}</td>
                  <td
                    className={
                      r.status === "absent"
                        ? "text-danger fw-bold"
                        : r.status === "late"
                        ? "text-warning fw-bold"
                        : r.status === "schedule off"
                        ? "text-muted"
                        : "text-success fw-bold"
                    }
                  >
                    {r.status === "on-time" || r.status === "early"
                      ? "PRESENT"
                      : r.status === "absent"
                      ? "ABSENT"
                      : r.status === "schedule off"
                      ? "OFF"
                      : String(r.status || "N/A").toUpperCase()}
                  </td>

                  <td>
                    {isFuture ? (
                      <OverlayTrigger overlay={<Tooltip>Cannot edit future dates</Tooltip>}>
                        <span className="text-muted">
                          <i className="fa fa-lock"></i>
                        </span>
                      </OverlayTrigger>
                    ) : r.status !== "schedule off" ? (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => handleEditClick(r)}
                      >
                        <Icon icon="simple-line-icons:pencil" />
                      </Button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>

        {/* Summary */}
        {summary && (
  <div className="mt-5 pt-20 mb-20 border-top">
    <h5 className="mb-20 text-center fw-semibold text-dark">
      Payroll Summary
    </h5>

    <div className="row g-4">
      {/* First Row - Stats */}
      <div className="col-12">
        <div className="row g-3 text-center">
          <div className="col-6 col-md-3">
            <div className="p-3 bg-light border rounded">
              <div className="text-muted small">Total Hours</div>
              <div className="fw-bold fs-5 text-dark">
                {parseFloat(summary.total_hours || 0).toFixed(2)} <small className="text-muted">hr</small>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="p-3 bg-light border rounded">
              <div className="text-muted small">Approved Hours</div>
              <div className="fw-bold fs-5 text-dark">
                {parseFloat(summary.approved_hours || 0).toFixed(2)}
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="p-3 bg-light border rounded">
              <div className="text-muted small">Overtime Hours</div>
              <div className="fw-bold fs-5 text-warning">
                {parseFloat(summary.total_overtime || 0).toFixed(2)}
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="p-3 bg-light border rounded">
              <div className="text-muted small">Present Days</div>
              <div className="fw-bold fs-5 text-success">
                {summary.presentDays || 0} / {summary.workingDays || 0}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Second Row - Financial Summary (Clean Table Style) */}
      <div className="col-12">
        <div className="border rounded overflow-hidden shadow-sm p-20">
          <table className="table table-borderless mb-0 align-middle">
            <tbody>
              <tr className="bg-light">
                <td className="ps-4 py-3 fw-medium text-muted">Base Wage</td>
                <td className="text-end pe-4 py-3 fw-bold text-dark">
                  {formatMoney(summary.base_wage)}
                </td>
              </tr>
              <tr>
                <td className="ps-4 py-3 fw-medium text-muted border-top">
                  + Overtime Pay
                </td>
                <td className="text-end pe-4 py-3 text-success fw-bold border-top">
                  + {formatMoney(summary.overtime_wage || 0)}
                </td>
              </tr>
              <tr>
                <td className="ps-4 py-3 fw-medium text-muted border-top">
                  - Deductions
                </td>
                <td className="text-end pe-4 py-3 text-danger fw-bold border-top">
                  - {formatMoney(summary.total_deduction || 0)}
                </td>
              </tr>
              <tr className="bg-primary text-white">
                <td className="ps-4 py-4 fw-semibold fs-5">
                  Net Payable Amount
                </td>
                <td className="text-end pe-4 py-4 fw-bold fs-4">
                  {formatMoney(summary.wage || summary.net_wage)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer Info */}
      <div className="col-12 text-center text-muted small mt-3">
        <div className="d-flex justify-content-center gap-5 flex-wrap">
          <span><strong>Working Days:</strong> {summary.workingDays || 0}</span>
          <span><strong>Scheduled Off:</strong> {summary.offDays || 0}</span>
          <span><strong>Absent:</strong> {(summary.workingDays || 0) - (summary.presentDays || 0)}</span>
        </div>
      </div>
    </div>
  </div>
)}
      </div>

      {/* Edit Modal */}
      {editRecord && (
        <EditAttendanceModal
          record={editRecord}
          onClose={() => setEditRecord(null)}
          onUpdated={() => {
            setEditRecord(null);
            // Refresh data
            const refresh = async () => {
              const res = await fetch(
                `${API_URL}/api/attendance/timesheet-log/custom-range?employeeId=${employee}&startDate=${moment(startDate).format("YYYY-MM-DD")}&endDate=${moment(endDate).format("YYYY-MM-DD")}`
              );
              const json = await res.json();
              if (json.success && json.data) setData(json.data);
            };
            refresh();
          }}
        />
      )}
    </div>
  );
};

export default TimesheetCustomRangeView;